import { useAuth0 } from "@auth0/auth0-react";
import BottomArrowSvg from "assets/icons/svg/BottomArrowSvg";
import Button from "components/button/Button";
import { LabelField } from "components/LabelField";
import MemberService from "Lib/API/Member/index";
import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import Select, { components } from "react-select";
import AsyncSelect from "react-select/async";
import {
  AddRelatedContactAction,
  AddRelatedContactContainer,
  AddRelatedContactContent,
  AddRelatedContactContentField,
  AddRelatedContactTitle
} from "./Theme";

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <BottomArrowSvg label="Emoji" />
    </components.DropdownIndicator>
  );
};



const colourStyles = {
  control: (styles) => ({
    ...styles,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "150px",
    overflowY: "auto",
  }),
  option: (styles, { isSelected }) => ({
    ...styles,
    color: isSelected ? "#333" : "#333",
  }),
};

const AddRelatedContact = ({
  accountId,
  closeModal,
  formatOptionLabel,
  relationshipOptions = [],
  onAddRelatedContact
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [contacts, setContacts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [relationshipList, setRelationshipList] = useState(relationshipOptions);
  const [relationshipInput, setRelationshipInput] = useState("");
  const [contactSearch, setContactSearch] = useState("");


  const fetchContacts = async (inputValue = "", nextPage = 1) => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await MemberService.getContactsListForRelated({
        token,
        page: nextPage,
        perPage: 10,
        data: {
          account: accountId,
          search: inputValue || undefined,
        },
      });

      if (res?.payload?.data) {
        const newContacts = res.payload.data.map((contact) => ({
          label: contact.full_name,
          value: contact._id,
          account_id: contact.account_id,
        }));

        setContacts((prev) => (nextPage === 1 ? newContacts : [...prev, ...newContacts]));
        setHasMore(res.payload.pagination.current_page * 10 < res.payload.pagination.count);
        setPage(nextPage + 1);
        return newContacts;
      }
    } catch (error) {
      toastr.error("Error", error?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
    return [];
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const debouncedFetchContacts = debounce(async (inputValue, callback) => {
    setPage(1);
    setHasMore(true);
    const results = await fetchContacts(inputValue, 1);
    callback(results);
  }, 500);

  const handleScrollToBottom = async () => {
    if (hasMore) {
      await fetchContacts(contactSearch, page);
    }
  };

  const handleRelationshipBlur = () => {
    if (relationshipInput.trim() !== "" && !relationshipList.some((rel) => rel.value === relationshipInput)) {
      const newOption = { label: relationshipInput, value: relationshipInput };
      setRelationshipList((prev) => [...prev, newOption]);
      setSelectedRelationship(newOption);
    }

    setRelationshipInput("");
  };


  const onAddClicked = () => {
    onAddRelatedContact({
      contact: selectedContact,
      relationship: selectedRelationship,
    });
  };


  return (
    <AddRelatedContactContainer>
      <AddRelatedContactTitle>Add Related Contact</AddRelatedContactTitle>
      <AddRelatedContactContent>
        <AddRelatedContactContentField>
          <LabelField mandatory title="Relationship" className="mb-0" />
          <Select
            placeholder="Select or type relationship"
            classNamePrefix="react-select"
            options={relationshipList}
            styles={colourStyles}
            onChange={(value) => {
              setSelectedRelationship(value);
              setRelationshipInput("");
            }}
            value={selectedRelationship}
            components={{ DropdownIndicator }}
            onInputChange={(inputValue, actionMeta) => {
              if (actionMeta.action === "input-change") {
                setRelationshipInput(inputValue);
              }
            }}
            onBlur={handleRelationshipBlur}
            noOptionsMessage={() => null} 
            />
        </AddRelatedContactContentField>
        <AddRelatedContactContentField>
          <LabelField mandatory title="Contact" className="mb-0" />
          <AsyncSelect
            cacheOptions
            loadOptions={(inputValue, callback) => {
              setContactSearch(inputValue);
              debouncedFetchContacts(inputValue, callback);
            }}
            defaultOptions={contacts}
            onMenuScrollToBottom={handleScrollToBottom}
            isLoading={loading}
            placeholder="Search contact"
            inputValue={contactSearch}
            onInputChange={(inputValue, { action }) => {
              if (action !== "menu-close" && action !== "input-blur") {
                setContactSearch(inputValue);
              }
            }}
            styles={colourStyles}
            onChange={(value) => {
              setSelectedContact(value);
              setContactSearch("");
            }}
            value={selectedContact}
            classNamePrefix="react-select"
          />
        </AddRelatedContactContentField>
      </AddRelatedContactContent>

      <AddRelatedContactAction>
        <Button
          onClick={closeModal}
          variant="primary-outline"
          size="md"
          style={{ minWidth: "100px" }}
        >
          <span>Cancel</span>
        </Button>
        <Button
          onClick={onAddClicked}
          variant="primary"
          size="md"
          style={{ minWidth: "100px" }}
        >
          <span>Save</span>
        </Button>
      </AddRelatedContactAction>
    </AddRelatedContactContainer>
  );
};

export default AddRelatedContact;
